import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Download, Printer, ArrowLeft,
  Clock, Users, MapPin, Crown, Car, HelpCircle, Phone
} from 'lucide-react';
import { eventContent as c } from '../data/eventContent';

const QRCodePage = () => {
  const [downloading, setDownloading] = useState(false);

  const eventUrl = useMemo(() => {
    if (typeof window === 'undefined') return 'https://gracemadeus.com/event';
    const host = window.location.host.includes('gracemadeus.com')
      ? 'https://gracemadeus.com'
      : window.location.origin;
    return `${host}/event`;
  }, []);

  // Larger QR (~18% bigger than the previous 500/900/1500 set) with high
  // error correction so it scans crisply from a distance.
  const QR_RENDER = 1100;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=${QR_RENDER}x${QR_RENDER}&data=${encodeURIComponent(eventUrl)}&bgcolor=ffffff&color=1a120e&margin=4&qzone=4&ecc=H`;

  const quickLinks = [
    { icon: Clock,       label: 'Timeline',     anchor: '#day-timeline' },
    { icon: Users,       label: 'Wedding Party', anchor: '#party' },
    { icon: MapPin,      label: 'Venue',        anchor: '#welcome' },
    { icon: Crown,       label: 'Attire',       anchor: '#attire' },
    { icon: Car,         label: 'Parking',      anchor: '#guest-info' },
    { icon: HelpCircle,  label: 'FAQ',          anchor: '#event-faq' },
    { icon: Phone,       label: 'Contact',      anchor: '#guest-info' }
  ];

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const res = await fetch(qrSrc);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mary-arrington-wedding-qr.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => window.print();

  return (
    <div className="qrsign" data-testid="qr-page">
      {/* Engagement photo background — holding hands with the ring */}
      <picture className="qrsign-bg" aria-hidden="true">
        <source srcSet="/images/our-story-hands.webp" type="image/webp" />
        <img src="/images/our-story-hands.jpg" alt="" />
      </picture>
      <div className="qrsign-overlay" aria-hidden="true" />
      <div className="qrsign-grain" aria-hidden="true" />

      {/* Top toolbar (hidden on print) */}
      <div className="qrsign-toolbar no-print">
        <Link to="/" className="qrsign-back">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="qrsign-actions">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="qrsign-btn"
            data-testid="qr-download-btn"
          >
            <Download className="w-4 h-4" />
            <span>{downloading ? 'Downloading…' : 'Download QR'}</span>
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="qrsign-btn qrsign-btn-ghost"
            data-testid="qr-print-btn"
          >
            <Printer className="w-4 h-4" />
            <span>Print</span>
          </button>
        </div>
      </div>

      {/* The welcome sign */}
      <main className="qrsign-card-wrap">
        <article className="qrsign-card" data-testid="qrsign-card">
          {/* Editorial header */}
          <header className="qrsign-header">
            <span className="qrsign-flourish" aria-hidden="true">
              <svg viewBox="0 0 120 12" preserveAspectRatio="none">
                <path d="M0 6 H50" stroke="currentColor" strokeWidth="1" fill="none" />
                <circle cx="60" cy="6" r="2" fill="currentColor" />
                <path d="M70 6 H120" stroke="currentColor" strokeWidth="1" fill="none" />
              </svg>
            </span>
            <h1 className="qrsign-title">WELCOME</h1>
            <span className="qrsign-flourish" aria-hidden="true">
              <svg viewBox="0 0 120 12" preserveAspectRatio="none">
                <path d="M0 6 H50" stroke="currentColor" strokeWidth="1" fill="none" />
                <circle cx="60" cy="6" r="2" fill="currentColor" />
                <path d="M70 6 H120" stroke="currentColor" strokeWidth="1" fill="none" />
              </svg>
            </span>
            <p className="qrsign-instruction">
              Scan to View Our Wedding Guide
            </p>
          </header>

          {/* The QR — ivory panel, thin gold border, generous quiet zone */}
          <div className="qrsign-qr-frame">
            <a
              href={eventUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="qrsign-qr-link"
              aria-label="Open wedding guide"
            >
              <img
                src={qrSrc}
                alt="QR code linking to the Mary & Arrington wedding guide"
                className="qrsign-qr-img"
                draggable={false}
              />
            </a>
          </div>

          {/* What's in the guide */}
          <div className="qrsign-includes" data-testid="qrsign-includes">
            <p className="qrsign-includes-title">Your complete wedding guide includes:</p>
            <p className="qrsign-includes-list">
              Timeline <span className="qrsign-dot">•</span> Wedding Party <span className="qrsign-dot">•</span> Venue <span className="qrsign-dot">•</span> Attire <span className="qrsign-dot">•</span> Parking <span className="qrsign-dot">•</span> FAQs
            </p>
          </div>

          {/* Couple */}
          <div className="qrsign-couple">
            <p className="qrsign-names">
              Mary <span className="footer-ampersand-flame">&amp;</span> Arrington
            </p>
            <div className="qrsign-divider" aria-hidden="true" />
            <p className="qrsign-date">Saturday, July 31, 2027</p>
          </div>

          {/* Motto */}
          <p className="qrsign-motto">{c.motto}</p>
        </article>
      </main>

      <p className="qrsign-tip no-print">
        Print or display this sign at the venue · QR opens <strong>gracemadeus.com/event</strong>
      </p>
    </div>
  );
};

export default QRCodePage;
